import { useNotification } from 'hooks'
import s from '../../../commonStyles.modules.scss'
import { useMemo, useState } from 'react'
import { Table } from 'components/core-ui/Table'
import { columns, drawerFilters, toolbarActions } from './helpers'
import { makeCoreGetFileRequest, makeCoreGetRequest } from "utils/api";
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { useQuery } from 'react-query'
import { formatAndDownloadResponse } from 'utils/helpers'
import { useLocation } from "react-router-dom"

const ProductsWithDifferences = ({ isHistorical }) => {
  const location = useLocation()
  const { showNotification } = useNotification()
  const [tableItems, setTableItems] = useState<DSProductWithDifference[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [isExcelExportLoading, setIsExcelExportLoading] = useState<boolean>(false)

  const orderNumberFromNav = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const raw = params.get('filters')
    if (!raw) return ''

    try {
      const parsed = JSON.parse(decodeURIComponent(raw))
      return parsed?.orderNumber ?? ''
    } catch {
      return ''
    }
  }, [location.search])

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: isHistorical
            ? '/shop-reception/ds-product-with-difference-archive'
            : '/shop-reception/ds-product-with-difference',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `Error while fetching table data. ${formatErrorMessage(error)}`,
        type: 'error'
      })
      setIsLoading(false)
    }
  }

  const { data: shopCodes } = useQuery(
    ['shop-codes'],
    async () => {
      const result = await makeCoreGetRequest('/shop-reception/shops/codes')

      const resultOptions = result.list?.map(item => ({
        label: item.displayName,
        value: item.code
      }))
      return resultOptions || []
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching shop codes dropdown data. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const exportFileExcel = async (tableState) => {
    setIsExcelExportLoading(true)
    const { filters, order, orderBy } = tableState
    const url = toTableGETUrl({
      url: isHistorical
        ? '/shop-reception/ds-product-with-difference-archive/export'
        : '/shop-reception/ds-product-with-difference/export',
      tableFilters: filters,
      order,
      orderBy
    })

    try {
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response);
      setIsExcelExportLoading(false)
    } catch (error) {
      setIsExcelExportLoading(false)
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const toolbar = useMemo(() => toolbarActions({
      totalCount,
      exportFileExcel: exportFileExcel,
      isExcelExportLoading }),
    [totalCount, isExcelExportLoading])

  return (
    <div className={s.mainContainer}>
      <Table
        hideCheckbox
        columns={columns()}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        toolbarActions={toolbar}
        rowActionsWidth="130px"
        filters={drawerFilters(shopCodes, orderNumberFromNav)}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default ProductsWithDifferences
